import { useState, useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import { addTracked, loadTracked, saveTracked, calcPnlPips, persistSignal, clearSignals, clearTracked } from '@/lib/storage';
export function useSignals() {
    const [signals, setSignals] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('apex_flow_signals') ?? '[]');
        }
        catch {
            return [];
        }
    });
    const [marketData, setMarketData] = useState(null);
    const [prices, setPrices] = useState({});
    const [alerts, setAlerts] = useState([]);
    const [activeSignal, setActiveSignal] = useState(null);
    const [timeframe, setTimeframe] = useState('M15');
    const [trackedSignals, setTracked] = useState(() => loadTracked());
    const alertTimerRef = useRef(new Map());
    const handleMessage = useCallback((msg) => {
        switch (msg.type) {
            case 'signal': {
                const sig = msg.data;
                // Persist to localStorage (never auto-erased)
                const savedSignals = persistSignal(sig);
                setSignals(savedSignals);
                setActiveSignal(prev => prev ?? sig);
                // Auto-track every incoming 85%+ signal reactively
                const updated = addTracked(sig);
                setTracked(updated);
                break;
            }
            case 'market_data':
                setMarketData(msg.data);
                break;
            case 'price_update': {
                const pu = msg.data;
                setPrices(prev => ({ ...prev, [pu.pair]: pu }));
                // Auto-resolve pending signals when price hits TP/SL
                const current = loadTracked();
                const hasPending = current.some(t => t.signal.pair === pu.pair && t.outcome === 'pending');
                if (hasPending) {
                    let changed = false;
                    const resolved = current.map(t => {
                        if (t.signal.pair !== pu.pair || t.outcome !== 'pending')
                            return t;
                        const { signal } = t;
                        const p = pu.price;
                        let outcome = null;
                        if (signal.direction === 'LONG') {
                            if (p >= signal.tp3)
                                outcome = 'tp3';
                            else if (p >= signal.tp2)
                                outcome = 'tp2';
                            else if (p >= signal.tp1)
                                outcome = 'tp1';
                            else if (p <= signal.sl)
                                outcome = 'sl';
                        }
                        else {
                            if (p <= signal.tp3)
                                outcome = 'tp3';
                            else if (p <= signal.tp2)
                                outcome = 'tp2';
                            else if (p <= signal.tp1)
                                outcome = 'tp1';
                            else if (p >= signal.sl)
                                outcome = 'sl';
                        }
                        if (!outcome)
                            return t;
                        changed = true;
                        return { ...t, outcome, outcomeAt: Date.now(), pnlPips: calcPnlPips(signal, outcome) };
                    });
                    if (changed) {
                        saveTracked(resolved);
                        setTracked(resolved);
                    }
                }
                break;
            }
            case 'liquidity_alert': {
                const alert = msg.data;
                setAlerts(prev => [alert, ...prev].slice(0, 10));
                const key = `${alert.pair}_${alert.level}`;
                clearTimeout(alertTimerRef.current.get(key));
                alertTimerRef.current.set(key, setTimeout(() => {
                    setAlerts(prev => prev.filter(a => !(a.pair === alert.pair && a.level === alert.level)));
                }, 8_000));
                break;
            }
        }
    }, []);
    const { status, lastPing } = useWebSocket(handleMessage, timeframe);
    const clearAllSignals = () => {
        setSignals(clearSignals());
        setTracked(clearTracked());
        setActiveSignal(null);
    };
    return {
        signals, marketData, prices, alerts, activeSignal,
        wsStatus: status, lastPing,
        timeframe, setTimeframe,
        trackedSignals, setTracked,
        setActiveSignal, clearAllSignals,
    };
}
